import { useState, useEffect } from 'react';

interface StreakData {
  currentStreak: number;
  longestStreak: number;
  lastCompletionDate: string | null;
  totalCompleted: number;
  todayCompleted: number;
  dailyGoal: number;
}

const STREAK_KEY = 'checklist_streak_data';
const DEFAULT_DAILY_GOAL = 3;

const getToday = () => new Date().toISOString().split('T')[0];

const isYesterday = (dateStr: string) => {
  const date = new Date(dateStr);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return date.toISOString().split('T')[0] === yesterday.toISOString().split('T')[0];
};

const isToday = (dateStr: string | null) => {
  if (!dateStr) return false;
  return dateStr === getToday();
};

export const useStreak = () => {
  const [streakData, setStreakData] = useState<StreakData>(() => {
    const saved = localStorage.getItem(STREAK_KEY);
    if (saved) {
      const data = JSON.parse(saved) as StreakData;
      // Reset today's count if it's a new day
      if (!isToday(data.lastCompletionDate)) {
        // Check if streak should be reset (missed a day)
        if (data.lastCompletionDate && !isYesterday(data.lastCompletionDate) && !isToday(data.lastCompletionDate)) {
          return {
            ...data,
            currentStreak: 0,
            todayCompleted: 0,
          };
        }
        return { ...data, todayCompleted: 0 };
      }
      return data;
    }
    return {
      currentStreak: 0,
      longestStreak: 0,
      lastCompletionDate: null,
      totalCompleted: 0,
      todayCompleted: 0,
      dailyGoal: DEFAULT_DAILY_GOAL,
    };
  });

  useEffect(() => {
    localStorage.setItem(STREAK_KEY, JSON.stringify(streakData));
  }, [streakData]);

  const recordCompletion = () => {
    const today = getToday();
    
    setStreakData(prev => {
      const wasCompletedToday = isToday(prev.lastCompletionDate);
      const wasCompletedYesterday = prev.lastCompletionDate ? isYesterday(prev.lastCompletionDate) : false;
      
      let newStreak = prev.currentStreak;
      
      // If this is the first completion today
      if (!wasCompletedToday) {
        if (wasCompletedYesterday || prev.currentStreak === 0) {
          newStreak = prev.currentStreak + 1;
        } else {
          // Streak was broken, start fresh
          newStreak = 1;
        }
      }
      
      const newLongest = Math.max(prev.longestStreak, newStreak);
      const newTodayCompleted = wasCompletedToday ? prev.todayCompleted + 1 : 1;
      
      return {
        ...prev,
        currentStreak: newStreak,
        longestStreak: newLongest,
        lastCompletionDate: today,
        totalCompleted: prev.totalCompleted + 1,
        todayCompleted: newTodayCompleted,
      };
    });
  };

  const setDailyGoal = (goal: number) => {
    setStreakData(prev => ({ ...prev, dailyGoal: goal }));
  };

  const resetStreak = () => {
    setStreakData({
      currentStreak: 0,
      longestStreak: 0,
      lastCompletionDate: null,
      totalCompleted: 0,
      todayCompleted: 0,
      dailyGoal: DEFAULT_DAILY_GOAL,
    });
  };

  return {
    ...streakData,
    recordCompletion,
    setDailyGoal,
    resetStreak,
    goalProgress: Math.min((streakData.todayCompleted / streakData.dailyGoal) * 100, 100),
    goalReached: streakData.todayCompleted >= streakData.dailyGoal,
  };
};
